import { Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import theme from '../../assets/theme';
import useAccessBadge from '../../hooks/useAccessBadge';
import { useMetrics } from '../../hooks/useMetrics';
import { TTSState } from '../../hooks/useTextToSpeech';
import { SchoolPlanAccentIcon } from '../_atoms/SchoolPlanAccentIcon/SchoolPlanAccentIcon';
import { useSpaceTogglesPlayback } from './useSpaceTogglesPlayback';
import WaveformVisualizer from './WaveformVisualizer';

type FlagCode = 'US' | 'TX' | 'GB' | 'SCT' | 'AU';
type CountryGroup = 'US' | 'UK' | 'AU';

interface VoicePreset {
  name: string;
  flag: FlagCode;
  country: string;
  region: string;
  label: string;
  voiceId: string;
  initialVolume?: number;
}

const DEFAULT_VOICE_VOLUME = 0.6;

export const VOICE_PRESETS: VoicePreset[] = [
  {
    name: 'Hank',
    flag: 'TX',
    country: 'United States',
    region: 'Texas',
    label: 'Hank — Texas, US, Male',
    voiceId: '0GKwxxcRYcg0OlQ1l822',
    initialVolume: 0.45,
  },
  {
    name: 'Sir Reginald',
    flag: 'GB',
    country: 'United Kingdom',
    region: 'London',
    label: 'Sir Reginald — London, UK, Male',
    voiceId: 'BBfN7Spa3cqLPH1xAS22',
    initialVolume: 0.8,
  },
  {
    name: 'Josh',
    flag: 'US',
    country: 'United States',
    region: 'Teacher',
    label: 'Josh — Teacher, US, Male',
    voiceId: 'nzFihrBIvB34imQBuxub',
    initialVolume: 0.5,
  },
  {
    name: 'Piper',
    flag: 'SCT',
    country: 'Scotland',
    region: 'Glasgow',
    label: 'Piper — Glasgow, UK, Female',
    voiceId: 'InE4naNnowIxWA78Z5kE',
    initialVolume: 0.8,
  },
  {
    name: 'Bruce',
    flag: 'AU',
    country: 'Australia',
    region: 'Sydney',
    label: 'Bruce — Sydney, AU, Male',
    voiceId: 'aYIHaVW2uuV2iGj07rJH',
    initialVolume: 0.4,
  },
  {
    name: 'Victoria',
    flag: 'US',
    country: 'United States',
    region: '',
    label: 'Victoria — US, Female',
    voiceId: 'qSeXEcewz7tA0Q0qk9fH',
    initialVolume: 0.5,
  },
  {
    name: 'Addison',
    flag: 'AU',
    country: 'Australia',
    region: 'Melbourne',
    label: 'Addison — Melbourne, AU, Female',
    voiceId: 'eR40ATw9ArzDf9h3v7t7',
    initialVolume: 0.5,
  },
];

const FLAG_SRC: Record<FlagCode, string> = {
  US: '/flag-US.svg',
  TX: '/flag-TX.svg',
  GB: '/flag-GB.svg',
  SCT: '/flag-SCT.svg',
  AU: '/flag-AU.svg',
};

const groupForFlag = (flag: FlagCode): CountryGroup => {
  if (flag === 'US' || flag === 'TX') return 'US';
  if (flag === 'GB' || flag === 'SCT') return 'UK';
  return 'AU';
};

const findVoiceIdByName = (name: string): string =>
  VOICE_PRESETS.find(v => v.name === name)?.voiceId ?? VOICE_PRESETS[0].voiceId;

const groupForCountry = (country: string | undefined): CountryGroup | null => {
  if (!country) return null;
  const c = country.toUpperCase();
  if (c === 'US') return 'US';
  if (c === 'GB' || c === 'UK' || c === 'SCT') return 'UK';
  if (c === 'AU') return 'AU';
  return null;
};

const getDefaultVoiceId = (
  country: string | undefined,
  region: string | undefined,
): string => {
  const group = groupForCountry(country);
  if (group === 'UK') return findVoiceIdByName('Sir Reginald');
  if (group === 'AU') {
    const firstAU = VOICE_PRESETS.find(v => v.flag === 'AU');
    return firstAU?.voiceId ?? findVoiceIdByName('Josh');
  }
  if (group === 'US' && region?.toUpperCase() === 'TX') {
    return findVoiceIdByName('Hank');
  }
  return findVoiceIdByName('Josh');
};

const sortVoicesForUser = (group: CountryGroup | null): VoicePreset[] => {
  const baseOrder: CountryGroup[] = ['UK', 'AU', 'US'];
  const order: CountryGroup[] = group
    ? [group, ...baseOrder.filter(g => g !== group)]
    : baseOrder;
  const grouped = new Map<CountryGroup, VoicePreset[]>();
  for (const v of VOICE_PRESETS) {
    const g = groupForFlag(v.flag);
    const list = grouped.get(g) ?? [];
    list.push(v);
    grouped.set(g, list);
  }
  return order.flatMap(g => grouped.get(g) ?? []);
};

const AVATAR_TINTS = [
  '#F0CFC4',
  '#A1C6DA',
  '#9EBEB8',
  '#F0E1C4',
  '#BE9EBB',
  '#CCC3BA',
  '#FDE7D1',
];
const tintFor = (i: number) => AVATAR_TINTS[i % AVATAR_TINTS.length];

const formatLocation = (voice: VoicePreset) =>
  voice.region ? `${voice.region}, ${voice.country}` : voice.country;

const VOICE_DROPDOWN_MAX_HEIGHT = 285;

const SPEED_OPTIONS = Array.from({ length: 16 }, (_, i) => {
  const value = Math.round((0.5 + i * 0.1) * 10) / 10;
  const label = Number.isInteger(value) ? `${value}x` : `${value.toFixed(1)}x`;
  return { label, value };
});

interface Props {
  state: TTSState;
  error: string | null;
  analyserNode: AnalyserNode | null;
  volume: number;
  playbackRate: number;
  attachmentId: string;
  onGenerate: (voiceId: string) => void;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (delta: number) => void;
  onVolumeChange: (volume: number) => void;
  onPlaybackRateChange: (rate: number) => void;
}

const VoiceModeControls = ({
  state,
  error,
  analyserNode,
  volume,
  playbackRate,
  attachmentId,
  onGenerate,
  onPlay,
  onPause,
  onStop,
  onSeek,
  onVolumeChange,
  onPlaybackRateChange,
}: Props) => {
  const metrics = useMetrics();
  const { accessBadge } = useAccessBadge();
  const orgCountry = accessBadge?.organization?.country;
  const orgRegion = accessBadge?.organization?.region;
  const userCountryGroup = useMemo(() => groupForCountry(orgCountry), [orgCountry]);
  const sortedVoices = useMemo(() => sortVoicesForUser(userCountryGroup), [
    userCountryGroup,
  ]);

  const [selectedVoice, setSelectedVoice] = useState(findVoiceIdByName('Josh'));
  const userPickedVoiceRef = useRef(false);

  useEffect(() => {
    if (userPickedVoiceRef.current) return;
    if (!orgCountry) return;
    const defaultVoiceId = getDefaultVoiceId(orgCountry, orgRegion);
    setSelectedVoice(defaultVoiceId);
    const preset = VOICE_PRESETS.find(v => v.voiceId === defaultVoiceId);
    onVolumeChange(preset?.initialVolume ?? DEFAULT_VOICE_VOLUME);
  }, [orgCountry, orgRegion, onVolumeChange]);
  const [expanded, setExpanded] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voicePlacement, setVoicePlacement] = useState<'bottom' | 'top'>('bottom');
  const preMuteVolumeRef = useRef(volume || 0.6);
  const speedDropdownRef = useRef<HTMLDivElement>(null);
  const voiceDropdownRef = useRef<HTMLDivElement>(null);
  const voiceTriggerRef = useRef<HTMLButtonElement>(null);

  const getVoiceLabel = useCallback(
    (voiceId: string) => VOICE_PRESETS.find(v => v.voiceId === voiceId)?.label ?? voiceId,
    [],
  );

  const selectedVoicePreset = useMemo(
    () => sortedVoices.find(v => v.voiceId === selectedVoice) ?? sortedVoices[0],
    [selectedVoice, sortedVoices],
  );
  const selectedVoiceIndex = useMemo(
    () => sortedVoices.findIndex(v => v.voiceId === selectedVoice),
    [selectedVoice, sortedVoices],
  );

  const trackingProps = useMemo(
    () => ({
      attachmentId,
      voiceId: selectedVoice,
      voiceLabel: getVoiceLabel(selectedVoice),
    }),
    [attachmentId, selectedVoice, getVoiceLabel],
  );

  const handleMuteToggle = () => {
    if (volume > 0) {
      preMuteVolumeRef.current = volume;
      onVolumeChange(0);
    } else {
      onVolumeChange(preMuteVolumeRef.current);
    }
  };

  const isActive = state === 'playing' || state === 'paused' || state === 'loading';
  const showPanel = expanded || isActive;

  const handleStart = () => {
    const preset = VOICE_PRESETS.find(v => v.voiceId === selectedVoice);
    onVolumeChange(preset?.initialVolume ?? DEFAULT_VOICE_VOLUME);
    onGenerate(selectedVoice);
  };

  const handlePlayPause = (source: 'click' | 'space') => {
    if (state === 'playing') {
      onPause();
    } else {
      metrics.logEvent('TTS.Play', { ...trackingProps, source });
      onPlay();
    }
  };
  useSpaceTogglesPlayback(state, () => handlePlayPause('space'));

  const handleToggle = () => {
    if (isActive) {
      onStop();
      setExpanded(false);
    } else {
      setExpanded(prev => !prev);
    }
  };

  useEffect(() => {
    if (!speedOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        speedDropdownRef.current &&
        !speedDropdownRef.current.contains(e.target as Node)
      ) {
        setSpeedOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [speedOpen]);

  useEffect(() => {
    if (!voiceOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        voiceDropdownRef.current &&
        !voiceDropdownRef.current.contains(e.target as Node)
      ) {
        setVoiceOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [voiceOpen]);

  const handleVoiceChange = (voiceId: string) => {
    userPickedVoiceRef.current = true;
    setSelectedVoice(voiceId);
    const preset = VOICE_PRESETS.find(v => v.voiceId === voiceId);
    onVolumeChange(preset?.initialVolume ?? DEFAULT_VOICE_VOLUME);
    setVoiceOpen(false);
    if (isActive) {
      onStop();
      setExpanded(true);
    }
  };

  const handleVoiceTriggerClick = () => {
    if (state === 'loading') return;
    setVoiceOpen(open => {
      const next = !open;
      if (next) {
        const triggerRect = voiceTriggerRef.current?.getBoundingClientRect();
        if (triggerRect) {
          const spaceBelow = window.innerHeight - triggerRect.bottom;
          const spaceAbove = triggerRect.top;
          const desired = VOICE_DROPDOWN_MAX_HEIGHT + 12;
          if (spaceBelow < desired && spaceAbove > spaceBelow) {
            setVoicePlacement('top');
          } else {
            setVoicePlacement('bottom');
          }
        }
      }
      return next;
    });
  };

  return (
    <Container>
      <Tooltip title={showPanel ? 'Close' : 'Read Aloud'} placement="left">
        <TTSToggleButton onClick={handleToggle} $expanded={showPanel}>
          {showPanel ? <CloseIcon /> : <PlayIcon />}
        </TTSToggleButton>
      </Tooltip>

      <AnimatePresence>
        {showPanel && (
          <ExpandedPanelAnchor>
            <ExpandedPanel
              initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
              animate={{
                height: 'auto',
                opacity: 1,
                transitionEnd: { overflow: 'visible' },
              }}
              exit={{ height: 0, opacity: 0, overflow: 'hidden' }}
              transition={{ duration: 0.2 }}
            >
              <ExpandedPanelInner>
                <LabelRow>
                  <FieldLabel>Narrator</FieldLabel>
                  <Tooltip
                    title={
                      orgCountry === 'US'
                        ? 'Text-to-speech is available on the School plan'
                        : 'Text-to-speech is available on the Premium plan'
                    }
                    mouseEnterDelay={0.2}
                    placement="top"
                  >
                    <span>
                      <SchoolPlanAccentIcon size={16} />
                    </span>
                  </Tooltip>
                  {state === 'playing' && (
                    <WaveformVisualizer analyserNode={analyserNode} isPlaying />
                  )}
                  <LabelSpacer />
                </LabelRow>
                <VoicePickerWrapper ref={voiceDropdownRef}>
                  <VoiceTrigger
                    ref={voiceTriggerRef}
                    type="button"
                    onClick={handleVoiceTriggerClick}
                    disabled={state === 'loading'}
                    $disabled={state === 'loading'}
                  >
                    <TriggerAvatar style={{ background: tintFor(selectedVoiceIndex) }}>
                      <TriggerFlag
                        src={FLAG_SRC[selectedVoicePreset.flag]}
                        alt=""
                        aria-hidden
                      />
                    </TriggerAvatar>
                    <TriggerText>
                      <TriggerName>{selectedVoicePreset.name}</TriggerName>
                      <TriggerSub>{formatLocation(selectedVoicePreset)}</TriggerSub>
                    </TriggerText>
                    <TriggerChevron>
                      <DropdownArrowIcon />
                    </TriggerChevron>
                  </VoiceTrigger>
                  <AnimatePresence>
                    {voiceOpen && (
                      <VoiceDropdownPanel
                        $placement={voicePlacement}
                        initial={{
                          opacity: 0,
                          y: voicePlacement === 'top' ? 4 : -4,
                        }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                          opacity: 0,
                          y: voicePlacement === 'top' ? 4 : -4,
                        }}
                        transition={{ duration: 0.14 }}
                      >
                        {sortedVoices.map((voice, idx) => {
                          const isSelected = voice.voiceId === selectedVoice;
                          return (
                            <VoiceRow
                              key={voice.voiceId}
                              type="button"
                              $selected={isSelected}
                              onClick={() => handleVoiceChange(voice.voiceId)}
                            >
                              <VoiceAvatar style={{ background: tintFor(idx) }}>
                                <VoiceFlag
                                  src={FLAG_SRC[voice.flag]}
                                  alt=""
                                  aria-hidden
                                />
                              </VoiceAvatar>
                              <VoiceText>
                                <VoiceName $selected={isSelected}>{voice.name}</VoiceName>
                                <VoiceSub>{formatLocation(voice)}</VoiceSub>
                              </VoiceText>
                            </VoiceRow>
                          );
                        })}
                      </VoiceDropdownPanel>
                    )}
                  </AnimatePresence>
                </VoicePickerWrapper>

                <ActionRow>
                  {state === 'loading' && (
                    <LoadingRow>
                      <SpinnerRing />
                      <LoadingText>Generating Audio</LoadingText>
                    </LoadingRow>
                  )}

                  {(state === 'idle' || state === 'error') && (
                    <GenerateButton onClick={handleStart}>
                      <PlayIcon /> Play
                    </GenerateButton>
                  )}

                  {(state === 'playing' || state === 'paused') && (
                    <TransportRow>
                      <Tooltip title={volume > 0 ? 'Mute' : 'Unmute'} placement="bottom">
                        <VolumeIconButton onClick={handleMuteToggle}>
                          {volume > 0 ? <VolumeIcon /> : <VolumeMutedIcon />}
                        </VolumeIconButton>
                      </Tooltip>
                      <VolumeSlider
                        type="range"
                        min={0}
                        max={1}
                        style={{ width: '25px' }}
                        step={0.05}
                        value={volume}
                        onChange={e => onVolumeChange(parseFloat(e.target.value))}
                      />
                      <SpeedContainer ref={speedDropdownRef}>
                        <Tooltip title="Change Speed" placement="bottom">
                          <SpeedButton onClick={() => setSpeedOpen(prev => !prev)}>
                            {`${
                              Number.isInteger(playbackRate)
                                ? playbackRate
                                : playbackRate.toFixed(1)
                            }x`}
                          </SpeedButton>
                        </Tooltip>
                        {speedOpen && (
                          <SpeedDropdown>
                            {SPEED_OPTIONS.map(opt => (
                              <SpeedOption
                                key={opt.value}
                                $active={opt.value === playbackRate}
                                onClick={() => {
                                  metrics.logEvent('TTS.ChangeSpeed', {
                                    ...trackingProps,
                                    speed: opt.value,
                                  });
                                  onPlaybackRateChange(opt.value);
                                  setSpeedOpen(false);
                                }}
                              >
                                {opt.label}
                              </SpeedOption>
                            ))}
                          </SpeedDropdown>
                        )}
                      </SpeedContainer>
                      <Tooltip title="Skip backward 5 seconds" placement="bottom">
                        <SeekButton
                          onClick={() => {
                            metrics.logEvent('TTS.SkipBackward', trackingProps);
                            onSeek(-5);
                          }}
                        >
                          <SkipBackwardIcon />
                        </SeekButton>
                      </Tooltip>
                      <Tooltip
                        title={state === 'playing' ? 'Pause' : 'Play'}
                        placement="bottom"
                      >
                        <PlayPauseButton onClick={() => handlePlayPause('click')}>
                          {state === 'playing' ? <PauseIcon /> : <PlayIcon />}
                        </PlayPauseButton>
                      </Tooltip>
                      <Tooltip title="Skip forward 5 seconds" placement="bottom">
                        <SeekButton
                          onClick={() => {
                            metrics.logEvent('TTS.SkipForward', trackingProps);
                            onSeek(5);
                          }}
                        >
                          <SkipForwardIcon />
                        </SeekButton>
                      </Tooltip>
                    </TransportRow>
                  )}
                </ActionRow>

                {error && <ErrorText>{error}</ErrorText>}
              </ExpandedPanelInner>
            </ExpandedPanel>
          </ExpandedPanelAnchor>
        )}
      </AnimatePresence>
    </Container>
  );
};

const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
    <path d="M4.25447 0.440985C2.45135 -0.593307 0.989502 0.253997 0.989502 2.33198V17.6666C0.989502 19.7466 2.45135 20.5928 4.25447 19.5595L17.6577 11.8729C19.4614 10.8382 19.4614 9.16191 17.6577 8.1275L4.25447 0.440985Z" />
  </svg>
);

const DropdownArrowIcon = () => (
  <svg width="10" height="6" viewBox="0 0 24 14" fill="currentColor">
    <path d="M13.6184 13.337L23.323 3.83427C23.5375 3.62592 23.7078 3.37803 23.824 3.10492C23.9402 2.83181 24 2.53887 24 2.243C24 1.94713 23.9402 1.65419 23.824 1.38108C23.7078 1.10796 23.5375 0.860081 23.323 0.651731C22.8941 0.234301 22.314 0 21.7094 0C21.1047 0 20.5246 0.234301 20.0958 0.65173L11.9934 8.58566L3.89101 0.65173C3.46217 0.2343 2.88207 0 2.2774 0C1.67273 0 1.09263 0.2343 0.66379 0.65173C0.45166 0.86115 0.283834 1.10951 0.169932 1.38258C0.0560309 1.65564 -0.00170333 1.94804 0.0000381972 2.243C-0.00170335 2.53796 0.0560308 2.83035 0.169932 3.10342C0.283834 3.37648 0.45166 3.62485 0.66379 3.83427L10.3683 13.337C10.5811 13.5471 10.8342 13.7138 11.1132 13.8276C11.3921 13.9414 11.6912 14 11.9934 14C12.2955 14 12.5947 13.9414 12.8736 13.8276C13.1525 13.7138 13.4057 13.5471 13.6184 13.337Z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <rect x="3" y="2" width="4" height="12" rx="1" />
    <rect x="9" y="2" width="4" height="12" rx="1" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3.7 3.7a1 1 0 0 1 1.4 0L8 6.6l2.9-2.9a1 1 0 1 1 1.4 1.4L9.4 8l2.9 2.9a1 1 0 0 1-1.4 1.4L8 9.4l-2.9 2.9a1 1 0 0 1-1.4-1.4L6.6 8 3.7 5.1a1 1 0 0 1 0-1.4z" />
  </svg>
);

const SKIP_ARROW_PATH =
  'm475.875 173.859a21.334 21.334 0 1 0-39.975 14.907 192.586 192.586 0 0 1-44.125 203c-74.875 74.854-196.688 74.854-271.542 0s-74.854-196.672 0-271.532c66.012-66.015 169.159-73.586 244.142-22.434l-19.042 6.347a21.335 21.335 0 1 0 13.5 40.479l55.3-18.437a21.342 21.342 0 0 0 14.25-24.016l-11.383-63.282a21.337 21.337 0 0 0-42 7.552l1.534 8.526c-90.141-53.884-209.142-42.244-286.472 35.099-91.489 91.494-91.489 240.37 0 331.864a234.7 234.7 0 0 0 385.813-248.073z';

const SkipBackwardIcon = () => (
  <svg width="22" height="22" viewBox="0 0 512 512" fill="currentColor">
    <g transform="translate(512, 0) scale(-1, 1)">
      <path d={SKIP_ARROW_PATH} />
    </g>
    <text
      x="256"
      y="280"
      textAnchor="middle"
      dy="0.25em"
      fontSize="210"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      5
    </text>
  </svg>
);

const SkipForwardIcon = () => (
  <svg width="22" height="22" viewBox="0 0 512 512" fill="currentColor">
    <path d={SKIP_ARROW_PATH} />
    <text
      x="256"
      y="280"
      textAnchor="middle"
      dy="0.25em"
      fontSize="210"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      5
    </text>
  </svg>
);

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinnerRing = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const PANEL_BG = 'rgba(17, 23, 29, 0.85)';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const TTSToggleButton = styled.div<{ $expanded: boolean }>`
  color: white;
  width: 100%;
  text-align: center;
  height: 50px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${({ $expanded }) => ($expanded ? '5px 5px 0 0' : '5px')};
  padding: 10px;
  cursor: pointer;
  user-select: none;
  background: ${({ $expanded }) => ($expanded ? PANEL_BG : 'transparent')};

  &:hover {
    background: ${({ $expanded }) => ($expanded ? 'rgba(0, 0, 0, 0.95)' : 'black')};
  }
`;

const ExpandedPanelAnchor = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
`;

const ExpandedPanel = styled(motion.div)`
  border-radius: 10px 0 10px 10px;
  background: ${PANEL_BG};
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);
`;

const ExpandedPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  white-space: nowrap;
  width: 210px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LabelSpacer = styled.div`
  flex: 1;
`;

const FieldLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 36px;
`;

const GenerateButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 36px;
  border-radius: 5px;
  background: ${theme.colors.orange};
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${theme.colors.orangeDark};
  }
`;

const VoicePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const VoiceTrigger = styled.button<{ $disabled: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px 6px 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 5px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  font-family: inherit;
  text-align: left;
  transition: background 0.18s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.14)'};
  }
`;

const TriggerAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const TriggerFlag = styled.img`
  width: 22px;
  height: 15px;
  border-radius: 3px;
  object-fit: cover;
  display: block;
  pointer-events: none;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const TriggerText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TriggerName = styled.span`
  font-weight: 900;
  font-size: 13px;
  color: white;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TriggerSub = styled.span`
  font-weight: 600;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
`;

const TriggerChevron = styled.span`
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 2px;
`;

const VoiceDropdownPanel = styled(motion.div)<{ $placement: 'top' | 'bottom' }>`
  position: absolute;
  ${({ $placement }) =>
    $placement === 'top' ? 'bottom: calc(100% + 6px);' : 'top: calc(100% + 6px);'}
  right: 0;
  width: 240px;
  max-width: calc(100vw - 32px);
  max-height: ${VOICE_DROPDOWN_MAX_HEIGHT}px;
  /* overflow-y: scroll (not auto) reserves the scrollbar gutter so it's always visible — matters on Windows where the OS default is overlay scrollbars */
  overflow-y: scroll;
  background: #1c2226;
  border-radius: 10px;
  padding: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 10px 30px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 20;

  scrollbar-width: thin;
  scrollbar-color: rgba(245, 239, 230, 0.3) transparent;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
    -webkit-appearance: none;
    appearance: none;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 6px 0;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(245, 239, 230, 0.3);
    border-radius: 999px;
    border: 2px solid #1c2226;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(245, 239, 230, 0.45);
  }
`;

const VoiceRow = styled.button<{ $selected: boolean }>`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 5px;
  background: ${({ $selected }) =>
    $selected ? 'rgba(249, 151, 70, 0.16)' : 'transparent'};
  transition: background 0.18s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(249, 151, 70, 0.16)' : 'rgba(249, 151, 70, 0.07)'};
  }
`;

const VoiceAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08), 0 1px 2px rgba(0, 0, 0, 0.4);
  overflow: hidden;
`;

const VoiceFlag = styled.img`
  width: 32px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
  display: block;
  pointer-events: none;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const VoiceText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const VoiceName = styled.span<{ $selected: boolean }>`
  font-weight: 900;
  font-size: 15px;
  color: ${({ $selected }) => ($selected ? '#FFA154' : '#F5EFE6')};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VoiceSub = styled.span`
  font-weight: 600;
  font-size: 12.5px;
  color: rgba(245, 239, 230, 0.55);
  margin-top: 2px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VolumeSlider = styled.input`
  flex: 1;
  height: 4px;
  appearance: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: white;
    border: none;
    cursor: pointer;
  }
`;

const VolumeIconButton = styled.div`
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const VolumeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.7)">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
  </svg>
);

const VolumeMutedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.7)">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const TransportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const SeekButton = styled.div`
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const PlayPauseButton = styled.div`
  color: black;
  background: white;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  transition: all 0.2s;
  display: flex;
  padding-left: 2px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }
`;

const SpeedContainer = styled.div`
  position: relative;
  margin-left: auto;
`;

const SpeedButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  flex-shrink: 0;
  border-radius: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const SpeedDropdown = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  background: rgba(17, 23, 29, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 5px;
  padding: 4px 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const SpeedOption = styled.div<{ $active: boolean }>`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  color: ${({ $active }) => ($active ? 'white' : 'rgba(255, 255, 255, 0.7)')};
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.12)' : 'transparent')};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingText = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 11px;
`;

export default VoiceModeControls;
